/**
 * Marketplace Service
 * Handles job opportunities and bid operations
 */

import { apiClient } from './api';
import type {
  JobOpportunity,
  JobOpportunitiesResponse,
  Bid,
  BidsResponse,
  SubmitBidRequest,
  SubmitBidResponse,
  PostJobOpportunityRequest,
  PostJobOpportunityResponse,
  AcceptBidResponse,
  RejectBidRequest,
  RejectBidResponse,
  WithdrawBidResponse,
  AwardBidRequest,
  AwardBidResponse,
  CancelJobOpportunityResponse,
} from '../types/marketplace';
import {
  ensureJobOpportunitiesResponse,
  ensureJobOpportunity,
  ensureMarketplaceBidsResponse,
  ensureSubmitBidResponse,
  ensureMarketplaceAcceptBidResponse,
  ensureRejectBidResponse,
  ensureWithdrawBidResponse,
  ensurePostJobOpportunityResponse,
  ensureAwardBidResponse,
  ensureCancelJobOpportunityResponse,
  ensureMarketplaceBidArrayResponse,
} from '@/lib/api/type-guards';

export const marketplaceService = {
  /**
   * Browse available job opportunities
   * Provider route - requires authentication
   */
  async getOpportunities(
    county?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<JobOpportunitiesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (county) {
      params.append('county', county);
    }

    const response = await apiClient.get<JobOpportunitiesResponse>(
      `/marketplace/opportunities?${params.toString()}`
    );
    return ensureJobOpportunitiesResponse(response);
  },

  /**
   * Get a specific job opportunity by ID
   */
  async getOpportunity(jobId: string): Promise<JobOpportunity> {
    const response = await apiClient.get<JobOpportunity>(`/marketplace/opportunities/${jobId}`);
    return ensureJobOpportunity(response);
  },

  /**
   * Submit a bid for a job opportunity
   * Provider route - requires authentication
   */
  async submitBid(data: SubmitBidRequest): Promise<SubmitBidResponse> {
    const response = await apiClient.post<SubmitBidResponse>('/marketplace/submit-bid', data);
    return ensureSubmitBidResponse(response);
  },

  /**
   * Get provider's submitted bids
   * Provider route - requires authentication
   */
  async getMyBids(
    status?: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<BidsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (status) {
      params.append('status', status);
    }

    const response = await apiClient.get<BidsResponse>(`/marketplace/my-bids?${params.toString()}`);
    return ensureMarketplaceBidsResponse(response);
  },

  /**
   * Withdraw a pending bid
   * Provider route - requires authentication
   */
  async withdrawBid(bidId: string): Promise<WithdrawBidResponse> {
    const response = await apiClient.post<WithdrawBidResponse>(`/marketplace/bids/${bidId}/withdraw`);
    return ensureWithdrawBidResponse(response);
  },

  /**
   * Accept a bid (homeowner action)
   * Requires authentication
   */
  async acceptBid(bidId: string): Promise<AcceptBidResponse> {
    const response = await apiClient.post<AcceptBidResponse>(`/marketplace/bids/${bidId}/accept`);
    return ensureMarketplaceAcceptBidResponse(response);
  },

  /**
   * Reject a bid (homeowner action)
   * Requires authentication
   */
  async rejectBid(bidId: string, data: RejectBidRequest): Promise<RejectBidResponse> {
    const response = await apiClient.post<RejectBidResponse>(`/marketplace/bids/${bidId}/reject`, data);
    return ensureRejectBidResponse(response);
  },

  /**
   * Post a new job opportunity
   * Admin route - requires admin authentication
   */
  async postJobOpportunity(data: PostJobOpportunityRequest): Promise<PostJobOpportunityResponse> {
    const response = await apiClient.post<PostJobOpportunityResponse>(
      '/marketplace/admin/opportunities',
      data
    );
    return ensurePostJobOpportunityResponse(response);
  },

  /**
   * Award a bid to a service provider
   * Admin route - requires admin authentication
   */
  async awardBid(data: AwardBidRequest): Promise<AwardBidResponse> {
    const response = await apiClient.post<AwardBidResponse>('/marketplace/admin/award', data);
    return ensureAwardBidResponse(response);
  },

  /**
   * Cancel a job opportunity
   * Admin route - requires admin authentication
   */
  async cancelJobOpportunity(jobId: string): Promise<CancelJobOpportunityResponse> {
    const response = await apiClient.delete<CancelJobOpportunityResponse>(
      `/marketplace/admin/opportunities/${jobId}`
    );
    return ensureCancelJobOpportunityResponse(response);
  },

  /**
   * Get bids for a specific job (admin view)
   * Admin route - requires admin authentication
   */
  async getJobBids(jobId: string): Promise<Bid[]> {
    const response = await apiClient.get<{ bids: Bid[] }>(
      `/marketplace/admin/opportunities/${jobId}/bids`
    );
    return ensureMarketplaceBidArrayResponse(response).bids;
  },
};
